import { MediaUIAttributes } from 'media-chrome/dist/constants.js';
import { CaptionsRenderer } from 'media-captions';
import {
  CAPTIONS_OFF,
  matchTrackForLanguage,
  readSettings,
} from '../utils/player-prefs.ts';

/** Media Chrome caption requests, i.e. the viewer changing captions themselves. */
const CAPTION_REQUEST_EVENTS = [
  'mediashowsubtitlesrequest',
  'mediadisablesubtitlesrequest',
  'mediatogglesubtitlesrequest',
] as const;

/**
 * A custom element for displaying captions using Vidstack's captions renderer.
 *
 * @attr {string} mediasubtitlesshowing - (read-only) List of currently showing subtitles/captions
 * @attr {string} mediasubtitleslist - (read-only) List of all available subtitles/captions
 */
export class BunnyCaptionsDisplay extends HTMLElement {
  private mediaElement: HTMLMediaElement | null = null;
  private activeTrack: TextTrack | null = null;
  private renderer: CaptionsRenderer | null = null;
  private handleTimeUpdate: (() => void) | null = null;
  private handleTracksChange: ((e: Event) => void) | null = null;
  private handleCaptionRequest: (() => void) | null = null;
  private captionRequestTarget: Element | null = null;
  private handleIOSFullscreenBegin: (() => void) | null = null;
  private handleIOSFullscreenEnd: (() => void) | null = null;
  private trackModeBeforeFullscreen: TextTrackMode | null = null;
  private streamVideoElement: HTMLElement | null = null;
  private nativeCaptionStyle: HTMLStyleElement | null = null;
  private isInitialized: boolean = false;

  static get observedAttributes() {
    return [
      MediaUIAttributes.MEDIA_SUBTITLES_SHOWING,
      MediaUIAttributes.MEDIA_SUBTITLES_LIST,
    ];
  }

  constructor() {
    super();

    // Add positioning and variable mapping styles
    this.style.cssText = `
      position: absolute;
      inset: 0;
      pointer-events: none;
      display: flex;
      font-family: var(--captions-font-family);
      
      /* Map our custom variables to Vidstack's variables */
      --cue-color: var(--captions-text-color);
      --cue-bg-color: var(--captions-background);
      --cue-font-size: var(--captions-font-size);
      --cue-font-family: var(--captions-font-family);
      --cue-text-shadow: var(--captions-text-shadow);
      --cue-text-align: var(--captions-text-align);
      --cue-bottom: 10%;
      --cue-position-align: center;
      --cue-display-align: center;
      --cue-transform: translateY(calc(var(--captions-bottom-offset, 0) * -1));
    `;

    // Bind methods
    this.handleCueChange = this.handleCueChange.bind(this);
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;

    if (name === MediaUIAttributes.MEDIA_SUBTITLES_SHOWING) {
      this.updateActiveTrack();
    }
  }

  private initialize() {
    // Prevent duplicate initialization
    if (this.isInitialized) return;

    const streamVideoEl = this.parentElement;
    if (!streamVideoEl) return;

    this.streamVideoElement = streamVideoEl;

    // Find the video element (might not be ready immediately)
    const videoEl = this.findVideoElement(streamVideoEl);

    if (videoEl) {
      this.setup(videoEl);
    } else if (this.isConnected) {
      // Video element not ready yet, retry after a short delay
      requestAnimationFrame(() => {
        if (!this.isInitialized && this.isConnected) {
          this.initialize();
        }
      });
    }
  }

  private findVideoElement(
    streamVideoEl: HTMLElement,
  ): HTMLMediaElement | null {
    const videoEl = streamVideoEl.shadowRoot?.querySelector('video') ||
      streamVideoEl.querySelector('video');
    return videoEl instanceof HTMLMediaElement ? videoEl : null;
  }

  private setup(videoEl: HTMLMediaElement) {
    this.mediaElement = videoEl;

    // Create and inject the style that hides native captions
    this.createNativeCaptionHidingStyle();

    // Initialize renderer with this element as container
    if (!this.renderer) {
      this.renderer = new CaptionsRenderer(this);
    }

    this.setupTrackListeners();
    this.setupIOSFullscreenListeners();
    this.isInitialized = true;
  }

  private createNativeCaptionHidingStyle() {
    if (!this.streamVideoElement?.shadowRoot) return;

    // Check if style already exists (avoid duplicates)
    const existingStyle = this.streamVideoElement.shadowRoot.querySelector(
      '#caption-hide-style',
    );
    if (existingStyle instanceof HTMLStyleElement) {
      this.nativeCaptionStyle = existingStyle;
      return;
    }

    // Create the style element that hides native captions
    // This ensures we use custom captions instead of native ones
    this.nativeCaptionStyle = document.createElement('style');
    this.nativeCaptionStyle.id = 'caption-hide-style';
    this.nativeCaptionStyle.textContent = `
      video::cue {
        visibility: hidden !important;
        opacity: 0 !important;
        background: transparent !important;
        color: transparent !important;
        text-shadow: none !important;
      }
    `;
    this.streamVideoElement.shadowRoot.appendChild(this.nativeCaptionStyle);
  }

  private setupTrackListeners() {
    if (!this.mediaElement) return;

    // Avoid duplicate listeners
    if (this.handleTracksChange || this.handleTimeUpdate) {
      return;
    }

    // Listen for track changes
    this.handleTracksChange = this.updateActiveTrack.bind(this);
    this.mediaElement.textTracks.addEventListener(
      'change',
      this.handleTracksChange,
    );
    // Tracks can arrive after the first pass (native HLS on Safari), so re-run the
    // resolver when they do.
    this.mediaElement.textTracks.addEventListener(
      'addtrack',
      this.handleTracksChange,
    );

    // Listen for time updates
    this.handleTimeUpdate = () => {
      if (this.renderer && this.mediaElement) {
        this.renderer.currentTime = this.mediaElement.currentTime;
      }
    };
    this.mediaElement.addEventListener('timeupdate', this.handleTimeUpdate);

    this.setupCaptionRequestListeners();

    // Initial track setup
    this.updateActiveTrack();
  }

  private setupIOSFullscreenListeners() {
    if (!this.mediaElement) return;

    // Avoid duplicate listeners
    if (this.handleIOSFullscreenBegin || this.handleIOSFullscreenEnd) {
      return;
    }

    // iOS uses webkitbeginfullscreen/webkitendfullscreen events on the video element
    // These events are iOS-specific and won't fire on Chrome/Desktop browsers
    this.handleIOSFullscreenBegin = () => {
      // iOS draws its own caption picker in native fullscreen, and selections made there
      // arrive as a plain TextTrackList change rather than a media-chrome request. Hand
      // control over on entry, or convergence would treat the viewer's native choice as
      // drift and restore the stored one.
      this.viewerChoseCaptions = true;

      // Remove the style that hides native captions so iOS can display them
      this.nativeCaptionStyle?.remove();

      // Enable the active track for iOS native caption display
      // iOS requires track.mode to be "showing" to display captions in fullscreen
      if (this.activeTrack) {
        this.trackModeBeforeFullscreen = this.activeTrack.mode;
        this.activeTrack.mode = 'showing';
      }

      // Hide custom captions when iOS enters native fullscreen
      this.style.display = 'none';
    };

    this.handleIOSFullscreenEnd = () => {
      // Restore the style that hides native captions
      if (
        this.nativeCaptionStyle &&
        !this.nativeCaptionStyle.parentNode &&
        this.streamVideoElement?.shadowRoot
      ) {
        this.streamVideoElement.shadowRoot.appendChild(this.nativeCaptionStyle);
      }

      // Restore the original track mode if we changed it
      if (this.activeTrack && this.trackModeBeforeFullscreen !== null) {
        this.activeTrack.mode = this.trackModeBeforeFullscreen;
        this.trackModeBeforeFullscreen = null;
      }

      // Show custom captions again when exiting iOS fullscreen
      this.style.display = 'flex';
    };

    // Listen for iOS-specific fullscreen events
    this.mediaElement.addEventListener(
      'webkitbeginfullscreen',
      this.handleIOSFullscreenBegin,
    );
    this.mediaElement.addEventListener(
      'webkitendfullscreen',
      this.handleIOSFullscreenEnd,
    );
  }

  /**
   * Set once the viewer changes captions themselves, after which their live choice
   * owns the state and we stop asserting the stored one.
   */
  private viewerChoseCaptions: boolean = false;

  private updateActiveTrack() {
    if (!this.mediaElement || !this.renderer) return;

    // Clean up previous track listener
    if (this.activeTrack) {
      this.activeTrack.removeEventListener('cuechange', this.handleCueChange);
    }

    // Filter by kind to exclude chapters and metadata tracks
    const captionTracks = Array.from(this.mediaElement.textTracks).filter(
      (track) => track.kind === 'subtitles' || track.kind === 'captions',
    );

    this.applyCaptionState(captionTracks);

    // Use the first showing track
    this.activeTrack =
      captionTracks.find((track) => track.mode === 'showing') || null;

    if (this.activeTrack) {
      this.activeTrack.addEventListener('cuechange', this.handleCueChange);
      this.handleCueChange(); // Initial render
    } else {
      this.renderer.reset();
    }
  }

  /**
   * Keep the caption state at what the embed and the stored preference ask for, until
   * the viewer changes it themselves.
   *
   * This converges on every track change rather than running once, because the browser
   * is also an actor here: WebKit auto-enables a track matching the user's preferred
   * language once they have interacted with captions anywhere, and it does so *after*
   * our first pass. A one-shot silently loses to it, so "captions off" would not stick
   * on Safari - the exact complaint this feature exists to fix.
   *
   * Fighting the viewer is not a risk: their own changes go through the media-chrome
   * request events below, which both record the new preference and hand over control.
   */
  private applyCaptionState(captionTracks: TextTrack[]) {
    if (this.viewerChoseCaptions) return;

    // Tracks are mirrored from <bunny-stream-video>'s light DOM by custom-media-element,
    // so an early pass can legitimately see none yet.
    if (captionTracks.length === 0) return;

    const target = this.resolveCaptionTarget(captionTracks);
    const showing = captionTracks.filter((track) => track.mode === 'showing');

    // Already correct - and this is the branch that terminates the change-event loop
    // our own mode writes provoke.
    if (
      showing.length === (target ? 1 : 0) &&
      showing[0] === (target ?? undefined)
    ) {
      return;
    }

    for (const track of captionTracks) {
      if (track !== target && track.mode === 'showing') {
        track.mode = 'disabled';
      }
    }
    if (target && target.mode !== 'showing') {
      target.mode = 'showing';
    }
  }

  /** The track that should be showing, or null for "captions off". */
  private resolveCaptionTarget(captionTracks: TextTrack[]): TextTrack | null {
    const controller = this.closest('media-controller');

    // `captions-forced` means the embed requested a language: the server has already
    // marked the matching track `default`, and a request matching nothing must leave
    // captions off. Either way the saved preference does not apply.
    if (!controller?.hasAttribute('captions-forced')) {
      const savedLanguage = readSettings(this).captions;

      if (savedLanguage === CAPTIONS_OFF) return null;

      if (savedLanguage) {
        const match = matchTrackForLanguage(captionTracks, savedLanguage);
        if (match) return match;
        // This video has no track for the saved language. Fall back to the default
        // without clearing the preference - the viewer may return to a video that has it.
      }
    }

    return this.defaultCaptionTrack(captionTracks);
  }

  /** The track the server marked `default`, if any. */
  private defaultCaptionTrack(captionTracks: TextTrack[]): TextTrack | null {
    if (!this.streamVideoElement) return null;

    // Scoped by kind: the storyboard track is also rendered with `default`
    // (player.vto), and an unscoped `track[default]` would pick it up.
    const defaultTrackEl = this.streamVideoElement.querySelector(
      'track[default][kind="captions"], track[default][kind="subtitles"]',
    );
    if (!(defaultTrackEl instanceof HTMLTrackElement)) return null;

    // Browsers normalise TextTrack.language casing and srclang values vary, so compare
    // languages case-insensitively or the default track silently fails to match.
    const defaultLanguage = defaultTrackEl.srclang.toLowerCase();
    return captionTracks.find((track) =>
      track.label === defaultTrackEl.label &&
      track.language.toLowerCase() === defaultLanguage
    ) ?? null;
  }

  /**
   * Hand caption control to the viewer the moment they change it themselves. Their
   * choice is recorded by bunny-media-controller from these same events.
   */
  private setupCaptionRequestListeners() {
    const controller = this.closest('media-controller');
    if (!controller || this.handleCaptionRequest) return;

    this.handleCaptionRequest = () => {
      this.viewerChoseCaptions = true;
    };
    // Hold the node we listened on: by cleanup time this element may already be
    // detached, and closest() would then find nothing to unsubscribe from.
    this.captionRequestTarget = controller;
    for (const eventName of CAPTION_REQUEST_EVENTS) {
      controller.addEventListener(eventName, this.handleCaptionRequest);
    }
  }

  private handleCueChange() {
    if (!this.activeTrack || !this.renderer) return;

    // Reset cues
    this.renderer.reset();

    const cues = this.activeTrack.activeCues;
    if (!cues || cues.length === 0) {
      return;
    }

    // Add current cues
    Array.from(cues).forEach((cue) => {
      if (cue instanceof VTTCue) {
        // Set default position alignment if not set
        if (!cue.positionAlign) {
          cue.positionAlign = 'auto';
        }
        this.renderer?.addCue(cue);
      }
    });
  }

  private cleanup() {
    if (this.activeTrack) {
      this.activeTrack.removeEventListener('cuechange', this.handleCueChange);
    }

    if (this.mediaElement) {
      if (this.handleTimeUpdate) {
        this.mediaElement.removeEventListener(
          'timeupdate',
          this.handleTimeUpdate,
        );
      }
      if (this.handleTracksChange) {
        this.mediaElement.textTracks?.removeEventListener(
          'change',
          this.handleTracksChange,
        );
        this.mediaElement.textTracks?.removeEventListener(
          'addtrack',
          this.handleTracksChange,
        );
      }

      if (this.handleIOSFullscreenBegin) {
        this.mediaElement.removeEventListener(
          'webkitbeginfullscreen',
          this.handleIOSFullscreenBegin,
        );
      }
      if (this.handleIOSFullscreenEnd) {
        this.mediaElement.removeEventListener(
          'webkitendfullscreen',
          this.handleIOSFullscreenEnd,
        );
      }
    }

    if (this.renderer) {
      this.renderer.destroy();
      this.renderer = null;
    }

    this.mediaElement = null;
    this.activeTrack = null;
    this.handleTimeUpdate = null;
    this.handleTracksChange = null;
    if (this.handleCaptionRequest && this.captionRequestTarget) {
      for (const eventName of CAPTION_REQUEST_EVENTS) {
        this.captionRequestTarget.removeEventListener(
          eventName,
          this.handleCaptionRequest,
        );
      }
    }
    this.handleCaptionRequest = null;
    this.captionRequestTarget = null;
    this.handleIOSFullscreenBegin = null;
    this.handleIOSFullscreenEnd = null;
    this.trackModeBeforeFullscreen = null;
    this.streamVideoElement = null;
    this.nativeCaptionStyle = null;
    this.isInitialized = false;
    this.viewerChoseCaptions = false;
  }
}

// Register the custom element
if (!customElements.get('bunny-captions-display')) {
  customElements.define('bunny-captions-display', BunnyCaptionsDisplay);
}

export default BunnyCaptionsDisplay;
